import { useState } from 'react';
import { toast } from 'sonner';

import type { UserWithRole } from '~entities/users/model/types';
import { useDeleteUser } from '~entities/users/api/delete-user';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithRole | null;
  onClose: () => void;
}

export const DeleteUserDialog = ({
  open,
  onOpenChange,
  user,
  onClose,
}: DeleteUserDialogProps) => {
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const deleteUserMutation = useDeleteUser();

  const handleClose = () => {
    setDeleteConfirmText('');
    onClose();
  };

  const handleDelete = () => {
    if (!user?.id) {
      toast.error('User ID is missing');
      return;
    }

    if (deleteConfirmText !== 'delete') {
      toast.error("Please type 'delete' to confirm");
      return;
    }

    const userName = `${user.firstName} ${user.lastName}`;

    deleteUserMutation.mutate(user.id, {
      onSuccess: () => {
        toast.success(`User ${userName} has been deleted successfully`);
        handleClose();
      },
      onError: () => {
        toast.error(`Failed to delete user ${userName}. Please try again.`);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete User</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete {user?.firstName} {user?.lastName}?
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className='mt-4'>
          <Input
            value={deleteConfirmText}
            onChange={(e) => {
              setDeleteConfirmText(e.target.value);
            }}
            onPaste={(e) => {
              e.preventDefault();
            }}
            placeholder="Type 'delete' to confirm"
          />
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant='destructive'
            disabled={deleteConfirmText !== 'delete' || deleteUserMutation.isPending}
            onClick={handleDelete}
          >
            {deleteUserMutation.isPending ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

